export const randomColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = 70 + Math.floor(Math.random() * 31);
  const lightness = 50 + Math.floor(Math.random() * 21);

  return hslToHex(hue / 360, saturation / 100, lightness / 100);
};

function hslToHex(h, s, l) {
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

const APCA_TRC = 2.4;
const APCA_RCO = 0.2126729;
const APCA_GCO = 0.7151522;
const APCA_BCO = 0.072175;
const APCA_NORM_BG = 0.56;
const APCA_NORM_TXT = 0.57;
const APCA_REV_TXT = 0.62;
const APCA_REV_BG = 0.65;
const APCA_BLACK_THRESHOLD = 0.022;
const APCA_BLACK_CLAMP = 1.414;
const APCA_SCALE = 1.14;
const APCA_LO_OFFSET = 0.027;
const APCA_LO_CLIP = 0.1;
const APCA_DELTA_Y_MIN = 0.0005;

const sRgbChannelToY = (channel) => Math.pow(channel / 255, APCA_TRC);

const relativeLuminance = ([r, g, b]) =>
  APCA_RCO * sRgbChannelToY(r) +
  APCA_GCO * sRgbChannelToY(g) +
  APCA_BCO * sRgbChannelToY(b);

const clampBlack = (y) =>
  y > APCA_BLACK_THRESHOLD
    ? y
    : y + Math.pow(APCA_BLACK_THRESHOLD - y, APCA_BLACK_CLAMP);

const apcaContrast = (textY, backgroundY) => {
  const txtY = clampBlack(textY);
  const bgY = clampBlack(backgroundY);

  if (Math.abs(bgY - txtY) < APCA_DELTA_Y_MIN) return 0;

  if (bgY > txtY) {
    const sapc =
      (Math.pow(bgY, APCA_NORM_BG) - Math.pow(txtY, APCA_NORM_TXT)) *
      APCA_SCALE;
    return (sapc < APCA_LO_CLIP ? 0 : sapc - APCA_LO_OFFSET) * 100;
  }

  const sapc =
    (Math.pow(bgY, APCA_REV_BG) - Math.pow(txtY, APCA_REV_TXT)) * APCA_SCALE;
  return (sapc > -APCA_LO_CLIP ? 0 : sapc + APCA_LO_OFFSET) * 100;
};

const hexToRgb = (hexColor) => {
  const hex = hexColor.replace("#", "");
  return [
    parseInt(hex.substring(0, 2), 16),
    parseInt(hex.substring(2, 4), 16),
    parseInt(hex.substring(4, 6), 16),
  ];
};

export const getContrastingTextColor = (hexColor) => {
  if (!hexColor) return "#FFFFFF";

  const backgroundY = relativeLuminance(hexToRgb(hexColor));
  const blackContrast = Math.abs(apcaContrast(relativeLuminance([0, 0, 0]), backgroundY));
  const whiteContrast = Math.abs(
    apcaContrast(relativeLuminance([255, 255, 255]), backgroundY)
  );

  return blackContrast >= whiteContrast ? "#000000" : "#FFFFFF";
};

export const toSixDigitHex = (color) => {
  if (!color?.startsWith("#")) return color;
  const hex = color.slice(1);
  let r,
    g,
    b,
    a = 1;

  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 4) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
    a = parseInt(hex[3] + hex[3], 16) / 255;
  } else if (hex.length === 6) {
    return color.toUpperCase();
  } else {
    return color;
  }

  const blend = (c) => Math.round(c * a + 255 * (1 - a));
  return (
    "#" +
    [blend(r), blend(g), blend(b)]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()
  );
};
